import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { TabView, TabPanel } from 'primereact/tabview';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { SelectButton } from 'primereact/selectbutton';
import { Button } from 'primereact/button';
import PageHeader from '../common/PageHeader';
import LoadingState from '../common/LoadingState';
import EmptyState from '../common/EmptyState';
import RunDetail from './RunDetail';
import LoanLookup from './LoanLookup';
import AllocationLookup from './AllocationLookup';
import { WINDOWS, StatePill, fmtDateTime, fmtAgo, errMsg, cachedGet } from './shared';
import './activity.css';

const STATE_FILTERS = [
    { label: 'All', value: null },
    { label: 'Failed', value: 'failed' },
    { label: 'Stuck', value: 'stuck' },
    { label: 'Succeeded', value: 'success' },
];

const TYPE_LABELS = {
    SME: 'SME loanbook', CONSUMER: 'Consumer loanbook', CASH_FLOWS: 'Cash flows', OPPORTUNITIES: 'New loans (opportunities)',
    INVESTOR_SLICE: 'Investment slices', ECS: 'ESG portfolio',
};

// "Did we receive data?" - one tile per data type the platform has sent.
const Received = ({ platform, refreshKey }) => {
    const [data, setData] = useState(null);
    useEffect(() => {
        setData(null);
        cachedGet(`/activity/platforms/${encodeURIComponent(platform)}/received`, { params: { days: 90 }, force: refreshKey > 0 })
            .then((res) => setData(res.data.data_types)).catch(() => setData([]));
    }, [platform, refreshKey]);
    if (!data) return <LoadingState label="Checking deliveries…" size="sm" />;
    if (!data.length) return <EmptyState compact icon="pi-inbox" title="Nothing received in the last 90 days" />;
    return (
        <div className="act-received">
            {data.map((t) => (
                <div key={t.data_type} className="act-received-tile">
                    <div className="act-received-type">{TYPE_LABELS[t.data_type] || t.data_type}</div>
                    <div className="act-received-when">Last received {fmtAgo(t.last_received_at)} <StatePill state={t.last_state} /></div>
                    <small className="text-color-secondary">
                        {t.deliveries} in 90 days{t.failed ? ` · ${t.failed} failed` : ''}
                        {t.last_state === 'failed' && t.last_success_at && ` · last success ${fmtAgo(t.last_success_at)}`}
                    </small>
                </div>
            ))}
        </div>
    );
};

// One platform: did we receive its data, what happened to each delivery,
// and why its loans were or weren't bid.
const PlatformActivity = () => {
    const { platformKey } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const [days, setDays] = useState(30);
    const [stateFilter, setStateFilter] = useState(null);
    const [runs, setRuns] = useState(null);
    const [error, setError] = useState(null);
    const [expanded, setExpanded] = useState({});
    const [tab, setTab] = useState(0);
    const [refreshKey, setRefreshKey] = useState(0);
    const [bidMode, setBidMode] = useState('loan');
    const [openLoan, setOpenLoan] = useState(null);

    // ?run=<coll>/<key> deep link (the "Copy link" button) opens that run.
    const linkedRun = searchParams.get('run');

    const load = (force = false) => {
        setRuns(null);
        setError(null);
        if (force) setRefreshKey((k) => k + 1);
        cachedGet('/activity/runs', { params: { platform: platformKey, days, limit: 1000 }, force })
            .then((res) => setRuns(res.data.runs))
            .catch((err) => setError(errMsg(err)));
    };

    useEffect(() => load(), [platformKey, days]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (linkedRun) setExpanded({ [linkedRun]: true });
    }, [linkedRun]);

    const visible = useMemo(() => (runs || []).filter((r) => !stateFilter
        || (stateFilter === 'success' ? ['success', 'mapped'].includes(r.state) : r.state === stateFilter)), [runs, stateFilter]);
    const name = runs?.[0]?.platform || platformKey;

    const onRowToggle = (e) => {
        setExpanded(e.data);
        if (searchParams.get('run')) setSearchParams({});
    };

    return (
        <div className="content-container">
            <PageHeader
                title={name}
                subtitle="What we received from this Originator, what happened to it, and why loans were or weren't bid."
                icon="pi-building"
                actions={<Button label="All Originators" icon="pi pi-arrow-left" className="p-button-text" onClick={() => navigate('/activity')} />}
            />

            <section className="ex-section">
                <div className="ex-section-header"><div className="ex-section-title">Did we receive data?</div></div>
                <Received platform={platformKey} refreshKey={refreshKey} />
            </section>

            <TabView activeIndex={tab} onTabChange={(e) => setTab(e.index)}>
                <TabPanel header="Files & data">
                    <div className="ex-toolbar">
                        <div className="ex-toolbar-group">
                            <SelectButton value={stateFilter} options={STATE_FILTERS} onChange={(e) => setStateFilter(e.value)} />
                            <Dropdown value={days} options={WINDOWS.concat([{ label: 'Last 365 days', value: 365 }])} onChange={(e) => setDays(e.value)} />
                        </div>
                        <Button icon="pi pi-refresh" className="p-button-text" onClick={() => load(true)} tooltip="Refresh" />
                    </div>
                    {error && <div className="ex-inline-error">{error}</div>}
                    {!runs && !error && <LoadingState label="Loading deliveries…" />}
                    {runs && !visible.length && <EmptyState icon="pi-inbox" title="No deliveries" message="Nothing matches this filter in the selected period." />}
                    {runs && visible.length > 0 && (
                        <DataTable value={visible} dataKey="id" size="small" responsiveLayout="scroll" paginator rows={25}
                            expandedRows={expanded} onRowToggle={onRowToggle}
                            rowExpansionTemplate={(r) => <RunDetail runId={r.id} />}>
                            <Column expander style={{ width: '3rem' }} />
                            <Column header="Received" body={(r) => <span title={fmtDateTime(r.received_at)}>{fmtAgo(r.received_at)}</span>} />
                            <Column header="Data" body={(r) => TYPE_LABELS[String(r.data_type).toUpperCase()] || r.data_type} />
                            <Column header="File" body={(r) => <span className="act-file">{r.file_name || '—'}</span>} />
                            <Column header="Result" body={(r) => <StatePill state={r.state} />} />
                            <Column header="Why it failed" body={(r) => (r.error_headline
                                ? r.error_headline
                                : r.state === 'stuck' ? 'No result after 3 hours' : '')} />
                        </DataTable>
                    )}
                </TabPanel>
                <TabPanel header="Loans & bids">
                    {tab === 1 && (
                        <>
                            <div className="ex-toolbar">
                                <div className="ex-toolbar-group">
                                    <span>Look up</span>
                                    <SelectButton value={bidMode} onChange={(e) => e.value && setBidMode(e.value)}
                                        options={[{ label: 'A loan', value: 'loan' }, { label: 'An allocation', value: 'allocation' }]} />
                                </div>
                            </div>
                            {bidMode === 'loan'
                                ? <LoanLookup platform={platformKey} initialLoanId={openLoan} />
                                : <AllocationLookup platform={platformKey} onOpenLoan={(id) => { setOpenLoan(id); setBidMode('loan'); }} />}
                        </>
                    )}
                </TabPanel>
            </TabView>
        </div>
    );
};

export default PlatformActivity;
